import { useMutation } from '@apollo/client';

import { useDutchiePay } from 'src/payments/hooks';
import useUser from 'hooks/use-user';
import { EntityTypes, UnenrollPayByBankUserDocument } from 'src/gql/graphql';
import { getLogger } from 'src/utils/datadog-logger';
import { LOG_NAMESPACES } from 'src/payments/constants';
import { tracker } from 'src/utils/analytics';
import type { PbbIdentityChangedField, PbbUnenrollmentReason, PbbUnenrollmentSurface } from 'src/utils/analytics';

const logger = getLogger(LOG_NAMESPACES.PBB);

export type PbbUnenrollmentMetadata = {
  changedFields?: PbbIdentityChangedField[];
  reason: PbbUnenrollmentReason;
  surface?: PbbUnenrollmentSurface;
};

export type UseUnenrollPayByBankUserReturn = {
  handleUnenrollPayByBank: (metadata: PbbUnenrollmentMetadata) => Promise<{ success: boolean }>;
  isEnrolledDutchiePay: boolean;
};

export const useUnenrollPayByBankUser = (): UseUnenrollPayByBankUserReturn => {
  const User = useUser();
  const { isEnrolledDutchiePay } = useDutchiePay();
  const [unenrollPayByBankUser] = useMutation(UnenrollPayByBankUserDocument);

  const handleUnenrollPayByBank = async ({
    changedFields = [],
    reason,
    surface,
  }: PbbUnenrollmentMetadata): Promise<{ success: boolean }> => {
    let success = false;

    try {
      const { data } = await unenrollPayByBankUser({
        variables: { changedFields, entityId: User?.id, entityType: EntityTypes.user, reason, surface },
      });
      if (!data?.unenrollPayByBankUser.success) {
        throw new Error('Unenrollment failed');
      }
      User.resetUserPayByBankEnrollmentStatus();
      success = true;
    } catch (error) {
      console.error(error);
      logger.error('useUnenrollPayByBankUser Error', { error, userId: User?.id ?? 'unknown' });
    }

    try {
      tracker.dutchiePayUnenrollment({
        action: success ? 'succeeded' : 'failed',
        changedFields,
        reason,
        surface,
      });
    } catch {
      // Telemetry must not affect unenrollment.
    }

    return { success };
  };

  return { handleUnenrollPayByBank, isEnrolledDutchiePay };
};
